import { shadows } from '@alison/ui';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { MetricTrendPanel, type MetricTrendPanelProps, TREND_CARD_WIDTH } from './MetricTrendPanel';

const OPEN_DELAY_MS = 200;

const TREND_POPOVER_SX = { pointerEvents: 'none' };

const TREND_PAPER_SX = {
  pointerEvents: 'auto',
  width: TREND_CARD_WIDTH,
  maxWidth: '90vw',
  p: 3,
  bgcolor: 'background.paper',
  color: 'text.primary',
  borderRadius: 1,
  boxShadow: shadows.dropdown,
};

interface MetricTrendPopoverProps extends MetricTrendPanelProps {
  children: ReactNode;
}

export function MetricTrendPopover({
  scope,
  metric,
  currencySymbol,
  children,
}: MetricTrendPopoverProps): ReactNode {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(openTimer.current), []);

  function handleCellEnter(event: MouseEvent<HTMLElement>): void {
    const target = event.currentTarget;
    clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setAnchorEl(target), OPEN_DELAY_MS);
  }

  function handlePaperEnter(event: MouseEvent<HTMLElement>): void {
    clearTimeout(openTimer.current);
    setAnchorEl(event.currentTarget);
  }

  function handleClose(): void {
    clearTimeout(openTimer.current);
    setAnchorEl(null);
  }

  return (
    <Box sx={{ display: 'inline-flex' }} onMouseEnter={handleCellEnter} onMouseLeave={handleClose}>
      {children}
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        disableRestoreFocus
        sx={TREND_POPOVER_SX}
        slotProps={{
          paper: { sx: TREND_PAPER_SX, onMouseEnter: handlePaperEnter, onMouseLeave: handleClose },
        }}
      >
        <MetricTrendPanel scope={scope} metric={metric} currencySymbol={currencySymbol} />
      </Popover>
    </Box>
  );
}
